// La pantalla de BIENVENIDA del Agente: cuándo se muestra y cómo se cierra —
// ADR-2026-09-14-la-bienvenida-del-agente.
//
// Se muestra con DOS condiciones, y las dos son necesarias:
//
//   1. el usuario no tiene ningún chat propio (`usuarioTieneChats`) — un hecho de la cuenta,
//      que es lo que define "primera vez";
//   2. esta CUENTA, en este navegador, todavía no tocó "Genial, empecemos" (`localStorage`)
//      — lo que ESTE viewer ya vio.
//
// La segunda hace falta porque el panel lateral y la pantalla dedicada son dos montajes de
// la misma conversación: sin ella, cerrar la bienvenida en el panel y expandir a `/agente`
// la volvía a mostrar, y el botón se leía como si no hubiera hecho nada. Un `useState` no
// alcanza por lo mismo.
//
// Y va POR CUENTA, no por navegador (ADR-2026-09-15-la-bienvenida-se-acuerda-por-cuenta):
// la condición 1 es un hecho de la cuenta, así que con una marca global la primera cuenta
// que tocaba el botón en esa compu le comía el saludo a todas las que entraran después. Por
// eso el hook recibe el `usuarioId` en vez de leerlo por su cuenta: es el mismo que el resto
// del Chat usa para cortar lo guardado (`sesionDelChat`), y mientras no resolvió no se sabe
// nada todavía.
//
// La condición 1 se pregunta UNA vez por montaje y sólo cuando hace falta (`activo` y con la
// cuenta ya sabida): el arranque existe mientras la conversación está vacía, y con un chat
// andando no hay nada que decidir. Mientras no se sabe devuelve `false`: la convención de
// todos estos gates es que la duda no dibuje nada — un saludo de "primera vez" parpadeando
// en la cuenta de alguien con cuarenta chats es peor que aparecer un instante después.

import { useCallback, useEffect, useState } from 'react';
import { usuarioTieneChats } from '../../pages/v2/agentChatApi';
import { guardarBienvenidaVista, leerBienvenidaVista } from '../interruptoresChat';

const storageLocal = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export interface BienvenidaDelAgente {
  /** ¿Se dibuja la pantalla de bienvenida en vez del arranque de siempre? */
  mostrar: boolean;
  /** "Genial, empecemos": la cierra y se acuerda. */
  empezar: () => void;
}

export function usePrimeraVezEnElAgente(
  activo: boolean,
  usuarioId: string | null,
): BienvenidaDelAgente {
  const [sinChats, setSinChats] = useState(false);
  /** Quién tocó el botón en ESTE montaje. Guardar el id y no un `true` es lo que hace que
   *  la marca no se arrastre si la cuenta cambia debajo. */
  const [cerradaPor, setCerradaPor] = useState<string | null>(null);

  // Se lee en el render y no en un `useState` inicial porque la cuenta llega después del
  // primer render (el entorno la resuelve async): un inicial se quedaría con el `false` de
  // "todavía no sé quién sos". Es un `getItem` y sólo mientras la conversación está vacía.
  const cerrada =
    usuarioId !== null &&
    (cerradaPor === usuarioId || leerBienvenidaVista(storageLocal(), usuarioId));

  useEffect(() => {
    if (!activo || !usuarioId || cerrada) return;
    let vivo = true;
    void usuarioTieneChats().then((tiene) => {
      // `null` (sin sesión) y `true` caen en lo mismo: no saludar.
      if (vivo && tiene === false) setSinChats(true);
    });
    return () => {
      vivo = false;
    };
  }, [activo, usuarioId, cerrada]);

  const empezar = useCallback(() => {
    if (!usuarioId) return;
    guardarBienvenidaVista(storageLocal(), usuarioId);
    setCerradaPor(usuarioId);
  }, [usuarioId]);

  return { mostrar: sinChats && !cerrada, empezar };
}
